import React, { FC, useEffect, useRef, useState } from 'react';
import {
  Accordion, AccordionSummary, AccordionDetails,
  Box, Typography, TextField, Stack, Button, CircularProgress, Alert, Chip, InputAdornment,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useLazyProviderSearchQuery, useAddOrgMutation, useGetOnboardingStatusQuery } from './healthWalletSlice';
import { BRAND_DARKER, ORG_SWAP_ANIMATION } from './healthWalletConstants';
import type { EpicFhirOrg, ProviderSearchHit } from '../../types/onboarding';

/**
 * "A MyChart provider missing? Click here" — the picker for a provider Kno2 discovery did not match.
 *
 * Kno2 patient discovery finds most providers, but not all: a patient can know they were treated
 * somewhere we never surfaced. This searches Epic's own published provider directory (re-hosted
 * server-side) and appends the chosen organization to this patient's discovery orgs — after which it
 * behaves exactly like a discovered org: same Connect button, same MyChart redirect, same retrieval.
 *
 * ONE component, mounted in BOTH the onboarding providers screen and Settings, collapsed behind the
 * affordance so it never competes with the orgs we did find. See project_hw_epic_direct_connect.
 */

const MIN_QUERY = 3;        // below this, trigram search returns noise
const DEBOUNCE_MS = 350;

export const MissingProviderPicker: FC<{
  /** Receives the new row in epic_fhir_orgs shape so the caller can patch its own cache. The caller
   *  also owns bringing that row into view — see the scroll/highlight in both mount points. */
  onAdded: (org: EpicFhirOrg) => void;
  /** Called with the existing index when the picked org was already in the list, so the caller can
   *  highlight that row instead of appearing to do nothing. */
  onAlreadyPresent?: (orgIndex: number) => void;
  /**
   * SETTINGS ONLY — when supplied, a successful add offers "Connect … and read records now?" right in
   * the success notice, so the patient can go straight into the MyChart login they came for.
   *
   * ⛔ Deliberately NOT passed by onboarding. There the providers screen IS the connect/skip list, the
   * gate already blocks Continue while any org needs auth, and the flow is a BATCH (connect all, then
   * one retrieval) — an immediate OAuth redirect mid-list would break the batch AND remove the chance
   * to Skip at the one moment a patient most wants it (they just realised they picked the wrong
   * Moffitt). See project_hw_epic_direct_connect.
   */
  onConnectNow?: (org: EpicFhirOrg) => void;
  /** SETTINGS ONLY — non-null while a sync is in flight: the offer is withheld and this is shown
   *  instead. Settings disables its retrieval buttons during a run to prevent concurrent workflows,
   *  and this offer must not become a second path around that guard. */
  connectBlockedReason?: string | null;
}> = ({ onAdded, onAlreadyPresent, onConnectNow, connectBlockedReason }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [addingId, setAddingId] = useState<string | null>(null);
  const [addError, setAddError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // The org just added, held so the success notice can offer to connect it (Settings only).
  const [justAdded, setJustAdded] = useState<EpicFhirOrg | null>(null);
  const debounce = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const [search, { data, isFetching, isError: isSearchError }] = useLazyProviderSearchQuery();
  const [addOrg] = useAddOrgMutation();
  // Subscribes to the SAME cache entry both mount points already hold, so this costs no request —
  // it is here only to notice when the org this notice is about stops existing.
  const { data: onboardingStatus } = useGetOnboardingStatusQuery();

  /**
   * ★ THE NOTICE MUST NOT OUTLIVE WHAT IT DESCRIBES (Leo 2026-10-04). Delete the org from its row and
   *   the picker still said "X added" with a live "sign in to X" button underneath — this component
   *   holds that state itself and nothing told it the row had gone. Clicking the button then started
   *   a login for a provider no longer on the list.
   * ★ Keyed on the ORG BEING ABSENT rather than on a removal event, so it is right however it went:
   *   the row's delete button, another tab, or a refetch that no longer returns it.
   */
  useEffect(() => {
    if (!justAdded) return;
    const stillListed = onboardingStatus?.epicFhirOrgs?.some(
      (o) => o.requestId === justAdded.requestId && o.index === justAdded.index
    );
    if (!stillListed) { setJustAdded(null); setNotice(null); }
  }, [onboardingStatus, justAdded]);

  // Debounced search. Trailing-edge only — firing per keystroke would hammer a trigram scan over
  // ~96k facility names for strings the patient is still halfway through typing.
  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    const term = q.trim();
    if (term.length < MIN_QUERY) return;
    debounce.current = setTimeout(() => { void search(term); }, DEBOUNCE_MS);
    return () => { if (debounce.current) clearTimeout(debounce.current); };
  }, [q, search]);

  const hits: ProviderSearchHit[] = data?.organizations ?? [];
  const term = q.trim();
  const searched = term.length >= MIN_QUERY && !isFetching && !isSearchError && data !== undefined;

  const handleAdd = async (hit: ProviderSearchHit) => {
    setAddError(null);
    setNotice(null);
    setJustAdded(null);
    setAddingId(hit.brandId);
    try {
      // No request id — the SERVER resolves which ias_requests row holds the org list. The client
      // cannot: dataRequestId is the ACTIVE request, which a per-org refresh turns into an
      // epic-workflow row with no orgs at all. See project_hw_epic_direct_connect.
      const res = await addOrg({ brandId: hit.brandId, facilityId: hit.facilityId }).unwrap();
      if (res.alreadyPresent) {
        setNotice(`${hit.brandName} is already in your list.`);
        onAlreadyPresent?.(res.orgIndex);
      } else {
        onAdded(res.org);
        // Just the fact. ⛔ NOT "connect it below" — that was the original wording and it misled: on a
        // long list the row it pointed at was off-screen, so it read as nothing having happened. The
        // pointer is now an ACTION (Settings' Connect button below) or a MOVE (onboarding flips to this
        // org's connect step), never a sentence asking the patient to go hunting.
        setNotice(`${hit.brandName} added.`);
        setJustAdded(res.org);
        setQ('');
      }
    } catch (e: any) {
      // ⛔ NEVER a log-only catch. A catch that only console.errors leaves the row spinner stuck
      // forever — the exact class-bug that stranded the discovery step on launch eve. The failure
      // MUST land in render-consumed state. Local flag (channel ②), not dataRequestStatus: this
      // belongs on the current step with context, not bounced to the retrieval screen.
      // See project_hw_onboarding_error_handling.
      setAddError(e?.data?.error || e?.data?.message || "We couldn't add that provider. Please try again.");
    } finally {
      setAddingId(null);
    }
  };

  return (
    <Accordion
      expanded={open}
      onChange={(_, v) => setOpen(v)}
      disableGutters
      elevation={0}
      sx={{ mt: 2, bgcolor: 'transparent', '&:before': { display: 'none' } }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0, minHeight: 0 }}>
        {/* Phrased as an offer of action rather than an invitation to complain — "I'm not seeing my
            providers" reads as a dead end, this reads as something we can do about it. */}
        {/* ★ "Click here" is an INVITATION TO OPEN, so it goes once the panel is open — at that
            point it is telling you to do something you have already done, and the search box right
            below it is the answer. The question stays: it is the heading for what is now showing. */}
        <Typography variant="body2" sx={{ fontWeight: 600, color: BRAND_DARKER }}>
          A MyChart provider missing?{open ? '' : ' Click here'}
        </Typography>
      </AccordionSummary>

      <AccordionDetails sx={{ px: 0, pt: 0 }}>
        <Typography variant="caption" sx={{ color: '#6b6b70', display: 'block', mb: 1.5 }}>
          Search for the hospital or clinic where you were treated. You'll sign in with that provider's
          MyChart, the same as the others.
        </Typography>

        <TextField
          fullWidth
          size="small"
          value={q}
          // Typing starts a new search, so the previous outcome stops applying — clear the notice AND
          // the org behind the Connect offer together, or the button would still point at the last add.
          onChange={(e) => { setQ(e.target.value); setNotice(null); setAddError(null); setJustAdded(null); }}
          placeholder="Hospital or clinic name"
          InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>) }}
        />

        {/* Success notice. In Settings it carries the connect offer, so the patient can go straight into
            the MyChart login they came here for; withheld while a sync is running so this never becomes
            a second path around Settings' no-concurrent-runs guard. */}
        {notice && (
          <Alert severity="success" sx={{ mt: 1.5 }}>
            {notice}
            {/* ★ THE SENTENCE IS THE ACTION (Leo 2026-10-04). This started as a filled "Connect now"
                button in the Alert's `action` slot, which MUI right-aligns on the FIRST line — so it
                sat above the sentence explaining it and read as unrelated furniture. Putting the
                whole sentence IN the control means the offer and the thing you press are one. */}
            {onConnectNow && justAdded && !connectBlockedReason && (
              <Button
                variant="outlined"
                size="small"
                fullWidth
                onClick={() => onConnectNow(justAdded)}
                sx={{
                  // borderRadius 50 = the pill shape the in-row Log in / Skip controls use, so this
                  // reads as the same family rather than a squarer one-off.
                  mt: 1, textTransform: 'none', borderRadius: 50, py: 0.75,
                  // Allowed to WRAP — this is a sentence, not a label, and MUI keeps button content
                  // on one line by default, which a long provider name would overflow.
                  whiteSpace: 'normal', lineHeight: 1.4,
                  fontSize: '0.78rem', fontWeight: 400,
                  // ⛔ NO colour override — `Get Health Records` (ProviderRefreshButton) sets none
                  // either, so both take the theme's primary and MATCH BY CONSTRUCTION. Pinning a hex
                  // here would match today and drift the day the theme moves.

                  /**
                   * ★ THE SAME SLIDE THE ONBOARDING LOOP USES when it advances to the next provider
                   *   (Leo 2026-10-04) — shared from healthWalletConstants, not a near-copy. Same
                   *   job in both places: something appeared where something else just was, and
                   *   without motion the change is instant and goes unread.
                   */
                  ...ORG_SWAP_ANIMATION,
                  // ★ SAME keyframes (spread above), SLOWER here — 400ms suits the guided loop,
                  //   where the swap is the direct answer to a click you just made. This arrives
                  //   while you are still reading the notice above it, so it has to travel into
                  //   view rather than already be there by the time you look.
                  animation: 'hwOrgIn 700ms ease-out',
                  // Motion here is an accent, never the message — the sentence says the same thing
                  // standing still, so honouring the OS setting costs nothing.
                  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                }}
              >
                Click here to sign in to {justAdded.displayName ?? 'this provider'} and read your records now
              </Button>
            )}
            {onConnectNow && justAdded && connectBlockedReason && (
              <Typography variant="caption" sx={{ display: 'block', mt: 0.25 }}>
                {connectBlockedReason}
              </Typography>
            )}
          </Alert>
        )}
        {addError && <Alert severity="error" sx={{ mt: 1.5 }}>{addError}</Alert>}
        {isSearchError && (
          <Alert severity="error" sx={{ mt: 1.5 }} action={<Button size="small" onClick={() => void search(term)}>Try again</Button>}>
            We couldn't search for providers just now.
          </Alert>
        )}

        {isFetching && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
            <CircularProgress size={16} />
            <Typography variant="caption" sx={{ color: '#6b6b70' }}>Searching…</Typography>
          </Stack>
        )}

        {/* The list is Epic-only, so a provider on another system legitimately returns nothing. Say so
            plainly and point at the route that does work, rather than leaving a blank panel that
            reads as "this is broken". */}
        {searched && hits.length === 0 && (
          <Alert severity="info" sx={{ mt: 1.5 }}>
            No match for “{term}”. Not every provider can be connected this way — you can still add
            records yourself by uploading them.
          </Alert>
        )}

        {searched && hits.length > 0 && (
          <Stack spacing={1} sx={{ mt: 1.5 }}>
            {hits.map((hit) => {
              // ★ The label is what the patient recognises; the brand is who actually holds the
              // records. Showing only the label would let someone think they had found Moffitt when
              // the match is a Tampa General clinic AT Moffitt's address. Name both whenever they differ.
              const differs = hit.brandName && hit.brandName !== hit.label;
              const busy = addingId === hit.brandId;
              // ★ THE WHOLE ROW ADDS, not just the button (Leo 2026-10-04) — a small outlined button
              //   at the end of a row whose name and address are the thing you were reading is a
              //   needlessly small target, on a phone most of all. The button STAYS: it is what says
              //   what the click will do, and it is the keyboard-obvious control.
              // ⚠️ Clickable ONLY when adding is actually what would happen. An already-added row and
              //   an unconnectable one are informational — giving them a pointer and a hover state
              //   would promise an action that does not exist.
              const rowAdds = hit.connectable && !hit.alreadyAdded && !busy;
              return (
                <Box
                  key={`${hit.brandId}:${hit.label}`}
                  // Real button semantics rather than a bare onClick: without these the row is
                  // mouse-only and invisible to a screen reader, which is a regression for anyone
                  // who was using the button perfectly well before.
                  role={rowAdds ? 'button' : undefined}
                  tabIndex={rowAdds ? 0 : undefined}
                  onClick={rowAdds ? () => void handleAdd(hit) : undefined}
                  onKeyDown={rowAdds ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); void handleAdd(hit); }
                  } : undefined}
                  sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, p: 1.25,
                        border: '1px solid #ececf1', borderRadius: '12px', bgcolor: 'white',
                        ...(rowAdds && {
                          cursor: 'pointer',
                          '&:hover': { bgcolor: '#f7f7fa', borderColor: '#d7d9e3' },
                          '&:focus-visible': { outline: `2px solid ${BRAND_DARKER}`, outlineOffset: 2 },
                        }) }}
                >
                  <LocalHospitalIcon fontSize="small" sx={{ color: '#9aa0b5', mt: 0.25 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{hit.label}</Typography>
                    {hit.address && (
                      <Typography variant="caption" sx={{ color: '#6b6b70', display: 'block' }}>
                        {hit.address}
                      </Typography>
                    )}
                    {differs && (
                      <Typography variant="caption" sx={{ color: '#6b6b70', display: 'block' }}>
                        Records held by <strong>{hit.brandName}</strong>
                      </Typography>
                    )}
                  </Box>
                  {hit.alreadyAdded ? (
                    // Already on their list. MARKED, never hidden or filtered out: a provider the patient
                    // went looking for must still appear — seeing it confirmed IS the answer they came
                    // for, and an absent row reads as "we don't have them". A positive chip rather than a
                    // disabled Add, so it never looks like the "Can't connect" state below.
                    <Chip size="small" color="success" variant="outlined" icon={<CheckCircleIcon />}
                          label="Already added" sx={{ fontSize: '0.7rem' }} />
                  ) : hit.connectable ? (
                    // stopPropagation: the row is clickable too, and without this a click on the
                    // button would fire both handlers — two add-org calls for one press.
                    <Button size="small" variant="outlined" disabled={busy}
                            onClick={(e) => { e.stopPropagation(); void handleAdd(hit); }}>
                      {busy ? <CircularProgress size={14} /> : 'Add'}
                    </Button>
                  ) : (
                    // Found, but Epic publishes no R4 endpoint for it — show it rather than hide it,
                    // so "we found your provider" is still the answer, just without a Connect.
                    <Chip size="small" icon={<BlockIcon />} label="Can't connect" sx={{ fontSize: '0.7rem' }} />
                  )}
                </Box>
              );
            })}
          </Stack>
        )}
      </AccordionDetails>
    </Accordion>
  );
};
